import React, { useState } from 'react';
import { Broker, Property } from '../App';
import Properties from './Properties';
import './BrokerPortal.css';

interface BrokerPortalProps {
  initialBroker?: Broker | null;
  initialProperties?: Property[];
}

const BrokerPortal: React.FC<BrokerPortalProps> = ({ initialBroker, initialProperties }) => {
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loggedInBroker, setLoggedInBroker] = useState<Broker | null>(initialBroker ?? null);
  const [allowedProperties, setAllowedProperties] = useState<Property[]>(initialProperties ?? []);

  const handleLogin = async () => {
    if (!loginUsername.trim() || !loginPassword) return;
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const resp = await fetch('/api/brokers/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUsername.trim(), password: loginPassword }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Login failed');
      }
      const data = await resp.json();
      setLoggedInBroker(data.broker);
      setAllowedProperties(data.properties);
    } catch (e: any) {
      setLoginError(e.message || 'Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const logout = () => {
    setLoggedInBroker(null);
    setAllowedProperties([]);
    setLoginUsername('');
    setLoginPassword('');
    setLoginError('');
  };

  const handleUpdateProperty = (updated: Property) => {
    setAllowedProperties((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  if (!loggedInBroker) {
    return (
      <div className="broker-portal">
        <div className="broker-login-card">
          <h2>Broker Portal</h2>
          <p>Sign in with broker credentials</p>
          <div className="broker-login-form">
            <input
              type="text"
              placeholder="Username"
              value={loginUsername}
              onChange={(e) => setLoginUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            <input
              type="password"
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            {loginError && <p className="broker-login-error">{loginError}</p>}
            <button className="broker-login-btn" onClick={handleLogin} disabled={isLoggingIn || !loginUsername.trim() || !loginPassword}>
              {isLoggingIn ? 'Signing in…' : 'Sign In'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="broker-portal broker-portal-authenticated">
      <Properties
        properties={allowedProperties}
        readOnly={true}
        useServerFiltering={false}
        brokerIdentity={{ id: loggedInBroker.id, name: loggedInBroker.name }}
        enableAddressLinks={true}
        onUpdateProperty={handleUpdateProperty}
        headerAction={
          <button className="broker-inline-logout-btn" onClick={logout}>Logout</button>
        }
      />
    </div>
  );
};

export default BrokerPortal;
